
import { dayjs } from '../../../lib/dayjs';
import { scaleCalculator, formatNumberWithDecimalPlaces, getCssVar } from '../../../utils';
import { valueFormatter } from './helper';

const getAlarmSideSpacing = (alarm?: [Date, Date | undefined], isZoomActive?: boolean) => {
  if (alarm && !isZoomActive) {
    return { data: [dayjs(alarm[0]).subtract(30, 'minute').toDate(), null] };
  }
  return {};
};
export const mountDataLine = ({
  name,
  color,
  data,
  unit,
  start,
  end,
  isConnectNulls,
}: MountDataLineProps) => {
  return {
    type: 'line',
    name,
    color,
    data: data.map(({ x, y }, index, arr) => {
      const { getValueScale } = scaleCalculator(unit.values, 10);

      if (index === 0 && (!start || dayjs(x).isBefore(start))) start = x;
      if (index === arr.length - 1 && (!end || dayjs(x).isAfter(end))) end = x;
      const allZeros = data.every((d) => d.y === 0);

      return [x, typeof y === 'number' && !allZeros ? getValueScale(y) : y];
    }),
    connectNulls: isConnectNulls || false,
    emphasis: { disabled: true },
    silent: true,
    symbol: 'circle',
    showSymbol: data.length < 100,
    symbolSize: 4,
    tooltip: {
      valueFormatter: (value: number | null) => valueFormatter(value, unit),
    },
  };
};
export const mountUnitAxis = ({ name, values, offset }: MountUnitAxisProps) => ({
  name,
  position: 'right',
  min: 0,
  max: 1,
  type: 'value',
  axisLabel: {
    show: true,
    formatter(value: number) {
      if (values.every((v) => v === 0)) {
        return value;
      }
      const { getScaledValue } = scaleCalculator(values, 10);
      return formatNumberWithDecimalPlaces(getScaledValue(value));
    },
    color: getCssVar('--gray-300'),
  },
  axisLine: {
    show: true,
    onZero: false,
    lineStyle: { color: getCssVar('--gray-700'), width: 1.5 },
  },
  axisTick: {
    show: true,
    length: 5,
    lineStyle: { color: getCssVar('--gray-700'), width: 1.5 },
  },
  animation: false,
  minorTick: { show: true },
  nameTextStyle: { color: getCssVar('--gray-300'), fontSize: 10, fontWeight: 'bold' },
  offset,
  splitLine: { show: true, lineStyle: { color: getCssVar('--gray-700') } },
});
export const mountRiskArea = ({ data }: MountRiskAreaProps) => ({
  name: 'risk area',
  type: 'line',
  animation: false,
  markArea: {
    itemStyle: { color: getCssVar('--red-500-15') },
    data: [data.map((item) => ({ xAxis: item || null }))],
  },
});

export const mountAlarmMarkers = ({
  alarm,
  index,
  isZoomActive,
  color,
  dateRange,
}: MountAlarmMarkersProps) => {
  const lineColor = color ?? 'red';
  const areaColor = color
    ? `${color}${Math.floor(0.3 * 255).toString(16)}`
    : getCssVar('--red-500-15');

  const isAlarmStartAfterDateRange = dayjs(alarm[0]).isSameOrAfter(dateRange.start);

  return {
    name: `alarm ${index}`,
    type: 'line',
    ...getAlarmSideSpacing(alarm, isZoomActive),
    tooltip: { show: false },

    markLine: isAlarmStartAfterDateRange
      ? {
          lineStyle: { color: lineColor, type: 'solid', width: 1 },
          symbol: ['none', 'none'],
          label: { show: false },
          data: [{ xAxis: alarm[0] }],
          silent: true,
        }
      : {},
    markArea: {
      itemStyle: { color: areaColor },
      data: [
        [
          { xAxis: isAlarmStartAfterDateRange ? alarm[0] : dateRange.start.toDate() },
          { xAxis: alarm[1] },
        ],
      ],
    },
  };
};

export const mountAlarmClosedAt = ({ alarm, index }: MountClosedAtAlarmMarkersProps) => {
  return {
    name: `alarm closed at ${index}`,
    type: 'line',
    tooltip: { show: false },
    markLine: {
      lineStyle: { color: '#2F5291', type: 'solid', width: 2 },
      symbol: ['none', 'none'],
      label: { show: false },
      data: [{ xAxis: alarm }],
      silent: true,
    },
  };
};

export const mountThresholdLine = ({ name, color, data, style }: MountThresholdLineProps) => ({
  type: 'line',
  name,
  color,
  data,
  lineStyle: { type: style, width: 2 },
  animation: false,
  emphasis: { disabled: true },
  tooltip: { show: false },
  silent: true,
  symbol: 'none',
});
